import Enumeration from '@enums/enum';
import RepaymentType from '../enums/repayment-type';

const MONTHS_PER_YEAR = 12;

/**
 * To calculate the percentage relative to a year, we divide the interest by 100
 * to make it decimal, then we devide that by 12 months. For the montly period
 * that would be (100*12=) 1200.
 */
const AnualPercentageByPeriodEnum = new Enumeration({
  MONTHLY: 1200,
  FORTNIGHTLY: 2600,
  WEEKLY: 5200,
});

/**
 * The default options to use when calculating the repayment value.
 */
const DEFAULT_OPTIONS = Object.freeze({
  period: AnualPercentageByPeriodEnum.MONTHLY,
});

/**
 * Calculates the monthly repayment given the enquiry and the offer.
 *
 * @param {Object} enquiry - The original enquiry.
 * @param {Object} offer - The offer to consider.
 * @return {Number} The monthly repayment value.
 */
export default function getMonthlyRepayment(enquiry, offer) {
  const interestRate = parseFloat(offer.interest_rate, 10);
  const { data } = enquiry.target_resources[0];
  const loanAmount = data.value;
  let loanTermYears = 30;
  if (data.preferences && data.preferences.loan_term_in_years) {
    loanTermYears = data.preferences.loan_term_in_years;
  }

  if (offer.repayment_type === RepaymentType.INTEREST_ONLY) {
    return getInterestOnlyValue({
      loanAmount,
      interestRate,
      ongoingFees: offer.ongoing_fees_waived ? 0 : parseFloat(offer.ongoing_fees_value, 10),
    });
  }

  return getPrincipalAndInterestValue({
    ...DEFAULT_OPTIONS,
    loanAmount,
    interestRate,
    loanTermYears,
  });
}

/**
 * Calculates the monthly repayment for a given loan amount, interest rate,
 * period modifier and loan term years, using the formula for "Principal and Interest"
 *
 * @param {Object} - The options and values to use (loanAmount, interestRate,
 *                   period and loanTermYears).
 * @return {Number} The monthly repayment value.
 * @private
 */
function getPrincipalAndInterestValue({ loanAmount, interestRate, period, loanTermYears }) {
  const term = loanTermYears * MONTHS_PER_YEAR;
  const interestRatePerPeriod = interestRate / period;

  const x1 = 1 + interestRatePerPeriod;
  const r = x1 ** term;

  return Math.round((loanAmount * (interestRatePerPeriod * r)) / (r - 1));
}

/**
 * Calculates the monthly repayment for a given loan amount, interest rate
 * and ongoing fees, using the formula for "Interest Only"
 *
 * @param {Object} - The options and values to use (loanAmount, interestRate, ongoingFees).
 * @return {Number} The monthly repayment value.
 * @private
 */
function getInterestOnlyValue({ loanAmount, interestRate, ongoingFees }) {
  return Math.round(loanAmount * (interestRate / 100 / 12) + (ongoingFees / 12));
}
